/**
 * Pure H OS · Sprint 2 · Overlay & form primitives (extension layer).
 *
 * Sprint 1's `lib.tsx` is FROZEN. This file adds only the primitives
 * lib.tsx does not provide and which Sprint 2 needs across multiple
 * services: Modal, Drawer, Kbd, ListRow, TabBar, Toggle, Field.
 *
 * Every primitive consumes the Executive Warm v1 tokens exclusively —
 * no new visual language is introduced.
 */
import { ReactNode, useEffect } from "react";
import { X } from "lucide-react";

// ── Modal ────────────────────────────────────────────────────────
export function Modal({
  open, onClose, children, maxWidth = 560, testId,
}: {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  maxWidth?: number;
  testId?: string;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      data-testid={testId}
      className="fixed inset-0 z-50 flex items-start justify-center pt-24 px-4"
      style={{ background: "rgba(31, 32, 37, 0.42)", backdropFilter: "blur(4px)" }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full"
        style={{
          maxWidth,
          background: "var(--ph-surface-card)",
          border: "1px solid var(--ph-border-subtle)",
          borderRadius: "var(--ph-radius-lg)",
          boxShadow: "var(--ph-shadow-elevated)",
        }}
      >
        {children}
      </div>
    </div>
  );
}

// ── Drawer (right slide-in) ─────────────────────────────────────
export function Drawer({
  open, onClose, title, children, width = 420, testId,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  width?: number;
  testId?: string;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      data-testid={testId}
      className="fixed inset-0 z-40 flex justify-end"
      style={{ background: "rgba(31, 32, 37, 0.36)" }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="h-full overflow-y-auto"
        style={{
          width,
          background: "var(--ph-surface-card)",
          borderLeft: "1px solid var(--ph-border-subtle)",
          boxShadow: "var(--ph-shadow-elevated)",
        }}
      >
        <div
          className="sticky top-0 z-10 flex items-center justify-between px-5 py-3"
          style={{
            background: "var(--ph-surface-card)",
            borderBottom: "1px solid var(--ph-border-subtle)",
          }}
        >
          <div
            style={{
              fontFamily: "var(--ph-display-font)",
              fontSize: "var(--ph-size-h3)",
              fontWeight: 600,
              color: "var(--ph-ink-strong)",
              letterSpacing: "var(--ph-tracking-title)",
            }}
          >
            {title}
          </div>
          <button
            data-testid="drawer-close"
            onClick={onClose}
            aria-label="Close"
            className="w-7 h-7 flex items-center justify-center transition-colors"
            style={{ borderRadius: "var(--ph-radius-sm)", color: "var(--ph-ink-muted)" }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "var(--ph-hover-bg)")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
          >
            <X size={14} />
          </button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

// ── Kbd (keyboard chord chip) ───────────────────────────────────
export function Kbd({ children }: { children: ReactNode }) {
  return (
    <kbd
      className="inline-flex items-center gap-0.5 px-1.5 py-0.5 font-medium"
      style={{
        fontSize: "var(--ph-size-caption)",
        fontFamily: "var(--ph-mono-font)",
        color: "var(--ph-ink-muted)",
        background: "var(--ph-surface-card)",
        border: "1px solid var(--ph-border-subtle)",
        borderRadius: "var(--ph-radius-sm)",
      }}
    >
      {children}
    </kbd>
  );
}

// ── ListRow (clickable, icon + label + trailing) ────────────────
export function ListRow({
  icon, label, sublabel, trailing, onClick, active = false, testId,
}: {
  icon?: ReactNode;
  label: ReactNode;
  sublabel?: ReactNode;
  trailing?: ReactNode;
  onClick?: () => void;
  active?: boolean;
  testId?: string;
}) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className="w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors"
      style={{
        background: active ? "var(--ph-hover-bg)" : "transparent",
        borderRadius: "var(--ph-radius-sm)",
        color: "var(--ph-ink-strong)",
      }}
      onMouseEnter={(e) => {
        if (!active) e.currentTarget.style.background = "var(--ph-hover-bg)";
      }}
      onMouseLeave={(e) => {
        if (!active) e.currentTarget.style.background = "transparent";
      }}
    >
      {icon && (
        <span
          className="flex items-center justify-center flex-shrink-0"
          style={{ width: 22, height: 22, color: "var(--ph-ink-muted)" }}
        >
          {icon}
        </span>
      )}
      <span className="flex-1 min-w-0">
        <span className="block text-sm font-medium truncate" style={{ color: "var(--ph-ink-strong)" }}>
          {label}
        </span>
        {sublabel && (
          <span className="block text-xs truncate mt-0.5" style={{ color: "var(--ph-ink-muted)" }}>
            {sublabel}
          </span>
        )}
      </span>
      {trailing && <span className="flex items-center gap-1 flex-shrink-0">{trailing}</span>}
    </button>
  );
}

// ── TabBar (segmented tabs) ────────────────────────────────────
export function TabBar<T extends string>({
  tabs, active, onChange, testId,
}: {
  tabs: { key: T; label: string; count?: number }[];
  active: T;
  onChange: (key: T) => void;
  testId?: string;
}) {
  return (
    <div
      data-testid={testId}
      className="inline-flex items-center gap-1 p-1"
      style={{
        background: "var(--ph-surface)",
        border: "1px solid var(--ph-border-subtle)",
        borderRadius: "var(--ph-radius-md)",
      }}
    >
      {tabs.map((t) => {
        const isActive = t.key === active;
        return (
          <button
            key={t.key}
            data-testid={`tab-${t.key}`}
            onClick={() => onChange(t.key)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors"
            style={{
              background: isActive ? "var(--ph-surface-card)" : "transparent",
              color: isActive ? "var(--ph-ink-strong)" : "var(--ph-ink-muted)",
              borderRadius: "var(--ph-radius-sm)",
              boxShadow: isActive ? "var(--ph-shadow-card)" : "none",
              border: isActive ? "1px solid var(--ph-border-subtle)" : "1px solid transparent",
            }}
          >
            {t.label}
            {typeof t.count === "number" && (
              <span
                className="inline-flex items-center justify-center px-1"
                style={{
                  minWidth: 18,
                  height: 16,
                  fontSize: "10px",
                  background: isActive ? "var(--ph-brand-tint)" : "var(--ph-neutral-tint)",
                  color: isActive ? "var(--ph-brand-primary)" : "var(--ph-ink-muted)",
                  borderRadius: "999px",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {t.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

// ── Toggle ──────────────────────────────────────────────────────
export function Toggle({
  checked, onChange, testId, label,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  testId?: string;
  label?: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      data-testid={testId}
      onClick={() => onChange(!checked)}
      className="relative inline-flex items-center transition-colors"
      style={{
        width: 34,
        height: 20,
        borderRadius: "999px",
        background: checked ? "var(--ph-brand-primary)" : "var(--ph-border-strong)",
      }}
    >
      <span
        className="absolute top-0.5 transition-transform"
        style={{
          left: 2,
          width: 16,
          height: 16,
          borderRadius: "999px",
          background: "var(--ph-surface-card)",
          transform: checked ? "translateX(14px)" : "translateX(0)",
          boxShadow: "0 1px 2px rgba(31, 32, 37, 0.15)",
        }}
      />
    </button>
  );
}

// ── Field (labeled row) ────────────────────────────────────────
export function Field({
  label, hint, children, inline = false,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
  inline?: boolean;
}) {
  return (
    <div
      className={inline ? "flex items-center justify-between gap-4 py-3" : "py-3"}
      style={{ borderBottom: "1px solid var(--ph-border-subtle)" }}
    >
      <div className={inline ? "flex-1 min-w-0" : "mb-2"}>
        <div
          className="text-sm font-medium"
          style={{ color: "var(--ph-ink-strong)" }}
        >
          {label}
        </div>
        {hint && (
          <div
            className="text-xs mt-0.5"
            style={{ color: "var(--ph-ink-muted)" }}
          >
            {hint}
          </div>
        )}
      </div>
      <div className={inline ? "flex-shrink-0" : ""}>{children}</div>
    </div>
  );
}

// ── Segmented (mini choice group) ──────────────────────────────
export function Segmented<T extends string>({
  options, value, onChange, testId,
}: {
  options: { value: T; label: string }[];
  value: T;
  onChange: (v: T) => void;
  testId?: string;
}) {
  return (
    <div
      data-testid={testId}
      className="inline-flex items-center p-0.5"
      style={{
        background: "var(--ph-surface)",
        border: "1px solid var(--ph-border-subtle)",
        borderRadius: "var(--ph-radius-sm)",
      }}
    >
      {options.map((o) => {
        const isActive = o.value === value;
        return (
          <button
            key={o.value}
            data-testid={`seg-${o.value}`}
            onClick={() => onChange(o.value)}
            className="px-2.5 py-1 text-xs font-medium transition-colors"
            style={{
              background: isActive ? "var(--ph-surface-card)" : "transparent",
              color: isActive ? "var(--ph-ink-strong)" : "var(--ph-ink-muted)",
              borderRadius: "3px",
              border: isActive ? "1px solid var(--ph-border-subtle)" : "1px solid transparent",
            }}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}
